import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { InfoCard, CopyTextButton } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';

function CodeBlock({ text }: { text: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        my: 1,
        backgroundColor: 'rgba(0,0,0,0.04)',
        borderRadius: 1,
        p: 1,
      }}
    >
      <Typography
        component="pre"
        variant="body2"
        sx={{
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
          flex: 1,
          m: 0,
        }}
      >
        {text}
      </Typography>
      <CopyTextButton text={text} />
    </Box>
  );
}

export function GcsBucketCard() {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations ?? {};

  if (annotations['gcp.com/service'] !== 'gcs') {
    return null;
  }

  const details = (entity.spec?.gcpDetails ?? undefined) as
    Record<string, unknown> | undefined;

  const {
    storageClass,
    locationType,
    versioning,
    retentionPeriodSeconds,
    publicAccessPrevention,
    kmsKeyName,
    lifecycleRuleCount,
  } = (details ?? {}) as {
    storageClass?: string;
    locationType?: string;
    versioning?: boolean;
    retentionPeriodSeconds?: number;
    publicAccessPrevention?: string;
    kmsKeyName?: string;
    lifecycleRuleCount?: number;
  };

  const bucketName = entity.metadata.name;
  const gcsUri = `gs://${bucketName}`;

  const retentionDays =
    retentionPeriodSeconds !== undefined
      ? Math.round(retentionPeriodSeconds / 86400)
      : undefined;

  return (
    <InfoCard title="GCS Bucket">
      <Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {storageClass && (
            <Chip label={storageClass} size="small" variant="outlined" />
          )}
          {locationType && (
            <Chip label={locationType} size="small" variant="outlined" />
          )}
          {versioning !== undefined && (
            <Chip
              label={`Versioning: ${versioning ? 'on' : 'off'}`}
              size="small"
              variant="outlined"
            />
          )}
          {publicAccessPrevention && (
            <Chip
              label={`Public Access: ${publicAccessPrevention}`}
              size="small"
              color={
                publicAccessPrevention === 'enforced' ? 'success' : 'warning'
              }
              variant="outlined"
            />
          )}
        </Box>

        <Typography variant="subtitle2">Bucket URI</Typography>
        <CodeBlock text={gcsUri} />

        {lifecycleRuleCount !== undefined && lifecycleRuleCount > 0 && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Lifecycle rules: {lifecycleRuleCount}
          </Typography>
        )}

        {kmsKeyName && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            CMEK: {kmsKeyName}
          </Typography>
        )}

        {retentionDays !== undefined && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Retention: {retentionDays} days
          </Typography>
        )}
      </Box>
    </InfoCard>
  );
}
